import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  LinearProgress,
  Tabs,
  Tab
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  People as PeopleIcon,
  Group as GroupIcon,
  Security as SecurityIcon
} from '@mui/icons-material';
import api, { Partner, Tenant, TenantCreateRequest, TenantUpdateRequest } from '../services/api';
import { usePermissions } from '../contexts/PermissionContext';
import { PermissionGate } from '../components/PermissionGate';
import { UserManagement } from '../components/UserManagement';
import { UsersView } from '../components/UsersView';
import AllowList from '../components/AllowList';
import TenantDeleteDialog from '../components/TenantDeleteDialog';
import { PageTransition } from '../components/PageTransition';
import { useNotification } from '../contexts/NotificationContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const Tenants: React.FC = () => {
  const { permissions } = usePermissions();
  const { showNotification } = useNotification();
  const [tabValue, setTabValue] = useState(0);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(false);

  // Tenant dialog state
  const [tenantDialogOpen, setTenantDialogOpen] = useState(false);
  const [editingTenant, setEditingTenant] = useState<Tenant | null>(null);
  const [tenantForm, setTenantForm] = useState({ name: '', partner_id: '' });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [tenantToDelete, setTenantToDelete] = useState<{ id: string; name: string } | null>(null);

  const showSnackbar = useCallback((message: string, severity: 'success' | 'error') => {
    showNotification(message, severity);
  }, [showNotification]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [partnersData, tenantsData] = await Promise.all([
        api.getPartners(),
        api.getTenants()
      ]);
      setPartners(partnersData);
      setTenants(tenantsData);
    } catch (error) {
      console.error('Error fetching data:', error);
      showSnackbar('Error fetching data', 'error');
    } finally {
      setLoading(false);
    }
  }, [showSnackbar]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  // Tenant management functions
  const handleTenantDialogOpen = (tenant?: Tenant) => {
    if (tenant) {
      setEditingTenant(tenant);
      setTenantForm({ name: tenant.name, partner_id: tenant.partner_id || '' });
    } else {
      setEditingTenant(null);
      // For partner admins, automatically set the partner_id to their partner
      const userPartner = permissions?.partner_permissions?.find(p => p.role === 'admin');
      setTenantForm({ name: '', partner_id: userPartner?.partner_id || '' });
    }
    setTenantDialogOpen(true);
  };

  const handleTenantDialogClose = () => {
    setTenantDialogOpen(false);
    setEditingTenant(null);
    setTenantForm({ name: '', partner_id: '' });
  };

  const handleTenantSubmit = async () => {
    if (!tenantForm.name.trim()) {
      showSnackbar('Please enter a tenant name', 'error');
      return;
    }

    try {
      if (editingTenant) {
        const updateRequest: TenantUpdateRequest = {
          name: tenantForm.name
        };
        await api.updateTenant(editingTenant.id, updateRequest);
        showSnackbar('Tenant updated successfully', 'success');
      } else {
        const createRequest: TenantCreateRequest = {
          name: tenantForm.name,
          partner_id: tenantForm.partner_id || undefined
        };
        await api.createTenant(createRequest);
        showSnackbar('Tenant created successfully', 'success');
      }
      handleTenantDialogClose();
      fetchData();
    } catch (error) {
      console.error('Error saving tenant:', error);
      showSnackbar('Error saving tenant', 'error');
    }
  };

  const handleTenantDelete = (tenantId: string) => {
    const tenant = tenants.find(t => t.id === tenantId);
    if (tenant) {
      setTenantToDelete({ id: tenant.id, name: tenant.name });
      setDeleteDialogOpen(true);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!tenantToDelete) return;
    
    try {
      // The dialog will handle the actual deletion with selected resources
      // We just need to refresh the data after successful deletion
      fetchData();
      showSnackbar('Tenant deleted successfully', 'success');
    } catch (error) {
      console.error('Error deleting tenant:', error);
      showSnackbar('Error deleting tenant', 'error');
    }
  };

  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
    setTenantToDelete(null);
  };

  const getPartnerName = (partnerId?: string) => {
    if (!partnerId) return 'No Partner';
    const partner = partners.find(p => p.id === partnerId);
    return partner ? partner.name : 'Unknown Partner';
  };

  return (
    <PermissionGate permission="manage_tenants" fallback={
      <Box sx={{ textAlign: 'center', py: 8 }}>
        <Typography variant="h4" color="text.secondary" gutterBottom>
          Access Denied
        </Typography>
        <Typography variant="body1" color="text.secondary">
          You need tenant management permissions to access this page.
        </Typography>
      </Box>
    }>
      <PageTransition>
        <Box>
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold', mb: 4 }}>
            Partner Management
          </Typography>

        {loading && (
          <LinearProgress sx={{ mb: 3 }} />
        )}

        <Paper sx={{ width: '100%' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="management tabs">
            <Tab 
              icon={<PeopleIcon />} 
              label="Tenants" 
              iconPosition="start"
            />
            <Tab 
              icon={<GroupIcon />} 
              label="Users" 
              iconPosition="start"
            />
            <Tab 
              icon={<SecurityIcon />} 
              label="Allow List" 
              iconPosition="start"
            />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6">Tenants</Typography>
              {permissions?.user.is_admin && (
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => handleTenantDialogOpen()}
                >
                  Add Tenant
                </Button>
              )}
            </Box>

            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Partner</TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {tenants.map((tenant) => (
                    <TableRow key={tenant.id}>
                      <TableCell>{tenant.name}</TableCell>
                      <TableCell>
                        <Chip 
                          label={getPartnerName(tenant.partner_id)} 
                          color={tenant.partner_id ? 'primary' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{tenant.id}</TableCell>
                      <TableCell align="right">
                        <IconButton onClick={() => handleTenantDialogOpen(tenant)}>
                          <EditIcon />
                        </IconButton>
                        <IconButton onClick={() => handleTenantDelete(tenant.id)}>
                          <DeleteIcon />
                        </IconButton>
                        <UserManagement
                          resourceType="tenant"
                          resourceId={tenant.id}
                          resourceName={tenant.name}
                          onUserAdded={fetchData}
                          onUserRemoved={fetchData}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <UsersView />
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <AllowList />
          </TabPanel>
        </Paper>

        {/* Tenant Dialog */}
        <Dialog open={tenantDialogOpen} onClose={handleTenantDialogClose} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingTenant ? 'Edit Tenant' : 'Add Tenant'}
          </DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              margin="dense"
              label="Tenant Name"
              fullWidth
              variant="outlined"
              value={tenantForm.name}
              onChange={(e) => setTenantForm({ ...tenantForm, name: e.target.value })}
              sx={{ mb: 2 }}
            />
            {!editingTenant && (
              <TextField
                margin="dense"
                label="Partner"
                fullWidth
                variant="outlined"
                value={getPartnerName(tenantForm.partner_id)}
                disabled
                helperText="Tenant will be created under your partner"
              />
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleTenantDialogClose}>Cancel</Button>
            <Button onClick={handleTenantSubmit} variant="contained">
              {editingTenant ? 'Update' : 'Create'}
            </Button>
          </DialogActions>
        </Dialog>

        <TenantDeleteDialog
          open={deleteDialogOpen}
          tenantId={tenantToDelete?.id || ''}
          tenantName={tenantToDelete?.name || ''}
          onClose={handleDeleteDialogClose}
          onDelete={handleDeleteConfirm}
        />

        </Box>
      </PageTransition>
    </PermissionGate>
  );
};

export default Tenants; 